import { ShipperAddress } from "api/rest/address/types";
import { ShipmentWeightUnit } from "app/pages/shipments/types/domain";
import { TrailerSize } from "types/TrailerSize";
import { formatTrailerType } from "utils/formatTrailerType";

import { LocationState, LoadAttributesState, PartnersState, TeamMembersState } from "../types";

/**
 * Formats a trailer size for display (e.g., "T_53" -> "53'")
 */
const formatTrailerSize = (size: TrailerSize): string => {
  if (size.startsWith("T_")) {
    return size.replace("T_", "") + "'";
  }
  if (size.startsWith("C_")) {
    return size.replace("C_", "") + "'";
  }
  if (size === "NA") {
    return "";
  }
  return size;
};

/**
 * Formats a weight unit for display (e.g., "KG" -> "kg", "LB" -> "lbs")
 */
const formatWeightUnit = (unit: ShipmentWeightUnit): string => {
  switch (unit) {
    case ShipmentWeightUnit.Kg:
      return "kg";
    case ShipmentWeightUnit.Lb:
      return "lbs";
    default:
      return (unit as string).toLowerCase();
  }
};

/**
 * Formats a location address into a short string (e.g., "San Francisco, CA")
 */
export const formatLocationShort = (address: ShipperAddress | null): string => {
  if (!address) return "";

  const parts = [address.city, address.province_code].filter(Boolean);
  return parts.join(", ");
};

/**
 * Formats location state into a descriptor for the location step
 * Primary: "San Francisco, CA to Nashville, TN"
 * Secondary: "992 miles" (when distance is available)
 */
export const formatLocationDescriptor = (
  locationState: LocationState,
  totalDistance?: number
): { primary: string; secondary?: string } => {
  const pickup = locationState.pickup?.address;
  const delivery = locationState.delivery?.address;

  if (!pickup || !delivery) {
    return { primary: "" };
  }

  const pickupLocation = formatLocationShort(pickup);
  const deliveryLocation = formatLocationShort(delivery);

  // Include intermediate stops count if any
  const intermediateStopsCount = locationState.intermediateStops.length;
  let primary = `${pickupLocation} to ${deliveryLocation}`;

  if (intermediateStopsCount > 0) {
    primary += ` (+${intermediateStopsCount} stop${intermediateStopsCount > 1 ? "s" : ""})`;
  }

  // Format distance if available (including zero)
  const secondary = totalDistance !== undefined ? `${totalDistance.toLocaleString()} miles` : undefined;

  return { primary, secondary };
};

/**
 * Formats all locations into a detailed list
 * Used for the review step or detailed views
 */
export const formatLocationList = (locationState: LocationState): string[] => {
  const locations: string[] = [];

  // Add pickup
  if (locationState.pickup?.address) {
    const pickup = formatLocationShort(locationState.pickup.address);
    if (pickup) locations.push(`Pickup: ${pickup}`);
  }

  // Add intermediate stops
  locationState.intermediateStops.forEach((stop, index) => {
    if (stop.address) {
      const stopLocation = formatLocationShort(stop.address);
      if (stopLocation) locations.push(`Stop ${index + 1}: ${stopLocation}`);
    }
  });

  // Add delivery
  if (locationState.delivery?.address) {
    const delivery = formatLocationShort(locationState.delivery.address);
    if (delivery) locations.push(`Delivery: ${delivery}`);
  }

  return locations;
};

/**
 * Formats load attributes state into a descriptor
 * Primary: "26' Dry" (trailer size and type)
 * Secondary: "with 2,000 lb of palletized boxes" (weight and packing info)
 */
export const formatLoadAttributesDescriptor = (
  loadAttributesState: LoadAttributesState
): { primary: string; secondary?: string } => {
  // Primary: trailer size and type
  let primary = "";
  if (loadAttributesState.trailerSize && loadAttributesState.trailerType) {
    const formattedSize = formatTrailerSize(loadAttributesState.trailerSize);
    const formattedType = formatTrailerType(loadAttributesState.trailerType);
    primary = formattedSize ? `${formattedSize} ${formattedType}` : formattedType;
  } else if (loadAttributesState.trailerType) {
    primary = formatTrailerType(loadAttributesState.trailerType);
  }

  // Secondary: weight and packing details
  const parts: string[] = [];

  // Add weight info
  if (loadAttributesState.weight && loadAttributesState.weight > 0) {
    const formattedWeight = loadAttributesState.weight.toLocaleString();
    const formattedUnit = formatWeightUnit(loadAttributesState.weightUnit);
    parts.push(`${formattedWeight} ${formattedUnit}`);
  }

  // Add packing method and commodity
  if (loadAttributesState.isPalletized) {
    let packingText = "palletized";
    if (loadAttributesState.commodities) {
      packingText = `palletized ${loadAttributesState.commodities}`;
    }
    parts.push(packingText);
  } else {
    let packingText = "floor loaded";
    if (loadAttributesState.commodities) {
      packingText = `floor loaded ${loadAttributesState.commodities}`;
    }
    parts.push(packingText);
  }

  const secondary = parts.length > 0 ? `with ${parts.join(" of ")}` : undefined;

  return { primary, secondary };
};

/**
 * Formats partners state into a descriptor
 * Shows first two partner names and count of additional
 * Example: "Global Shipping, Pacific Link +3"
 */
export const formatPartnersDescriptor = (partnersState: PartnersState): { primary: string; secondary?: string } => {
  const selectedCount = partnersState.selectedPartnerIds.length;

  if (selectedCount === 0) {
    return { primary: "" };
  }

  // Get selected partner names
  const selectedPartners = partnersState.availablePartners
    .filter((p) => partnersState.selectedPartnerIds.includes(p.id.toString()))
    .map((p) => p.company_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown");

  // Format: "Global Shipping, Pacific Link +3"
  if (selectedCount <= 2) {
    return { primary: selectedPartners.join(", ") };
  } else {
    const firstTwo = selectedPartners.slice(0, 2).join(", ");
    const remaining = selectedCount - 2;
    return { primary: `${firstTwo} +${remaining}` };
  }
};

/**
 * Formats team members state into a descriptor
 * Primary: Shows team member first names separated by commas
 * Example: "Courtney, Kristin" or "No members selected"
 */
export const formatTeamMembersDescriptor = (
  teamMembersState: TeamMembersState
): { primary: string; secondary?: string } => {
  const selectedCount = teamMembersState.selectedMemberIds.length;

  if (selectedCount === 0) {
    return { primary: "No members selected" };
  }

  // Safety check: ensure availableMembers exists and is an array
  if (!teamMembersState.availableMembers || !Array.isArray(teamMembersState.availableMembers)) {
    return { primary: `${selectedCount} team member${selectedCount === 1 ? "" : "s"} selected` };
  }

  // Get selected team member first names with null checks
  const selectedMembers = teamMembersState.availableMembers
    .filter((member) => {
      // Ensure member exists and has contactId
      if (!member || member.contactId == null) return false;
      return teamMembersState.selectedMemberIds.includes(member.contactId.toString());
    })
    .map((member) => member.firstName || member.email?.split("@")[0] || "Unknown");

  // If no matching members found, fall back to count
  if (selectedMembers.length === 0) {
    return { primary: `${selectedCount} team member${selectedCount === 1 ? "" : "s"} selected` };
  }

  // Join all first names with commas
  return {
    primary: selectedMembers.join(", "),
  };
};
